/**
 * Sui SDK service layer for Coffee Rewards.
 * Handles all on-chain reads (queries) and transaction building.
 */
import { TransactionBlock } from '@mysten/sui.js/transactions';
// Node global — absent in browsers. Without this import the attested-stamping
// path throws "Buffer is not defined" at runtime and the stamp never lands.
import { Buffer } from 'buffer';
import {
    PACKAGE_ID,
    LOYALTY_PROGRAM_ID,
    SHOP_REGISTRY_ID,
    BADGE_REGISTRY_ID,
    STAMP_AUTHORITY_ID,
    SUI_CLOCK_ID,
    MODULES,
    OBJECT_TYPES,
    EVENT_TYPES,
} from './contract-constants';
import { suiClient } from './sui-client';
import { addressesEqual } from '../lib/shop-address';

export { suiClient };

// =============================================================
// READ FUNCTIONS (no wallet needed)
// =============================================================

/**
 * Fetch all CoffeeShop shared objects on-chain
 */
export async function fetchAllShops() {
    const objects = await suiClient.getObject({
        id: SHOP_REGISTRY_ID,
        options: { showContent: true },
    });
    return objects;
}

/**
 * Fetch a specific CoffeeShop by ID
 */
export async function fetchShop(shopId: string) {
    const obj = await suiClient.getObject({
        id: shopId,
        options: { showContent: true, showType: true },
    });
    return obj;
}

/**
 * Fetch all CustomerShopAccounts owned by a specific address
 */
export async function fetchCustomerShopAccounts(ownerAddress: string) {
    const objects = await suiClient.getOwnedObjects({
        owner: ownerAddress,
        filter: { StructType: OBJECT_TYPES.CustomerShopAccount },
        options: { showContent: true, showType: true },
    });
    return objects.data;
}

/**
 * Fetch the CustomerProfile (global XP/level) owned by a specific address
 */
export async function fetchCustomerProfile(ownerAddress: string) {
    const objects = await suiClient.getOwnedObjects({
        owner: ownerAddress,
        filter: { StructType: OBJECT_TYPES.CustomerProfile },
        options: { showContent: true },
    });
    return objects.data.length > 0 ? objects.data[0] : null;
}

/**
 * Fetch all RewardNFTs owned by a customer (for Loot Bag)
 */
export async function fetchRewardNFTs(ownerAddress: string) {
    const objects = await suiClient.getOwnedObjects({
        owner: ownerAddress,
        filter: { StructType: OBJECT_TYPES.RewardNFT },
        options: { showContent: true },
    });
    return objects.data;
}

/**
 * Fetch all Purchase records owned by a customer
 */
export async function fetchPurchaseHistory(ownerAddress: string) {
    const objects = await suiClient.getOwnedObjects({
        owner: ownerAddress,
        filter: { StructType: OBJECT_TYPES.Purchase },
        options: { showContent: true },
    });
    return objects.data;
}

/**
 * Fetch all Vouchers owned by a customer
 */
export async function fetchVouchers(ownerAddress: string) {
    const objects = await suiClient.getOwnedObjects({
        owner: ownerAddress,
        filter: { StructType: OBJECT_TYPES.Voucher },
        options: { showContent: true },
    });
    return objects.data;
}

/**
 * Fetch all Badges owned by a customer
 */
export async function fetchBadges(ownerAddress: string) {
    const objects = await suiClient.getOwnedObjects({
        owner: ownerAddress,
        filter: { StructType: OBJECT_TYPES.Badge },
        options: { showContent: true },
    });
    return objects.data;
}

/**
 * Fetch all CoffeeShop objects (query by type across all shared objects)
 */
export async function queryAllCoffeeShops() {
    // Query events to find all registered shop IDs, then fetch them
    const events = await suiClient.queryEvents({
        query: { MoveEventType: EVENT_TYPES.ShopRegistered },
        limit: 50,
        order: 'descending',
    });

    const shopIds = events.data.map((e: any) => e.parsedJson?.shop_id).filter(Boolean);

    if (shopIds.length === 0) return [];

    const shops = await Promise.all(
        shopIds.map((id: string) =>
            suiClient.getObject({ id, options: { showContent: true } })
        )
    );

    return shops.filter(s => s.data);
}

/**
 * Find a CoffeeShop object ID by owner address (queries on-chain events).
 * Returns the object ID or null if not found.
 */
export async function findShopIdByOwner(ownerAddress: string): Promise<string | null> {
    try {
        const events = await suiClient.queryEvents({
            query: { MoveEventType: EVENT_TYPES.ShopRegistered },
            limit: 50,
            order: 'descending',
        });

        for (const event of events.data) {
            const parsed = (event as any).parsedJson;
            if (addressesEqual(parsed?.owner, ownerAddress) && parsed?.shop_id) {
                return parsed.shop_id;
            }
        }
    } catch (err) {
        console.warn('[sui-service] Failed to find shop by owner:', err);
    }
    return null;
}

/**
 * Fetch the LoyaltyProgram shared object
 */
export async function fetchLoyaltyProgram() {
    const obj = await suiClient.getObject({
        id: LOYALTY_PROGRAM_ID,
        options: { showContent: true },
    });
    return obj;
}

/**
 * Fetch ShopCapability objects owned by a shop owner
 */
export async function fetchShopCapabilities(ownerAddress: string) {
    const objects = await suiClient.getOwnedObjects({
        owner: ownerAddress,
        filter: { StructType: OBJECT_TYPES.ShopCapability },
        options: { showContent: true },
    });
    return objects.data;
}

// =============================================================
// TRANSACTION BUILDERS (return TransactionBlock to sign)
// =============================================================

/**
 * Build transaction: Register a new customer
 */
export function buildRegisterCustomerTx(referralCode: string = ''): TransactionBlock {
    const tx = new TransactionBlock();
    tx.moveCall({
        target: `${PACKAGE_ID}::${MODULES.LOYALTY}::register_customer`,
        arguments: [
            tx.object(LOYALTY_PROGRAM_ID),
            tx.pure(Array.from(new TextEncoder().encode(referralCode))),
            tx.object(SUI_CLOCK_ID),
        ],
    });
    return tx;
}

/**
 * Build transaction: Create a customer-shop account
 */
export function buildCreateShopAccountTx(
    customerObjectId: string,
    shopOwnerAddress: string,
): TransactionBlock {
    const tx = new TransactionBlock();
    tx.moveCall({
        target: `${PACKAGE_ID}::${MODULES.LOYALTY}::create_shop_account`,
        arguments: [
            tx.object(customerObjectId),
            tx.pure(shopOwnerAddress),
            tx.object(SUI_CLOCK_ID),
        ],
    });
    return tx;
}

/**
 * Build transaction: Record a purchase (called by shop owner/barista)
 * Contract signature: record_purchase(account, profile, shop, program, clock, ctx)
 */
export function buildRecordPurchaseTx(
    accountObjectId: string,
    profileObjectId: string,
    shopObjectId: string,
): TransactionBlock {
    const tx = new TransactionBlock();
    tx.moveCall({
        target: `${PACKAGE_ID}::${MODULES.LOYALTY}::record_purchase`,
        arguments: [
            tx.object(accountObjectId),
            tx.object(profileObjectId),
            tx.object(shopObjectId),
            tx.object(LOYALTY_PROGRAM_ID),
            tx.object(SUI_CLOCK_ID),
        ],
    });
    return tx;
}

/**
 * Build transaction: Record multiple purchases in a single tx (batch stamping).
 * Uses record_batch_purchases() for quantity > 1, record_purchase() for quantity == 1.
 */
export function buildBatchRecordPurchaseTx(
    accountObjectId: string,
    profileObjectId: string,
    shopObjectId: string,
    quantity: number,
): TransactionBlock {
    const tx = new TransactionBlock();
    const count = Math.min(Math.max(1, quantity), 5); // clamp 1-5

    console.log('[buildBatchRecordPurchaseTx] Building for quantity:', count);

    if (count > 1) {
        // Use batch function for quantity > 1
        tx.moveCall({
            target: `${PACKAGE_ID}::${MODULES.LOYALTY}::record_batch_purchases`,
            arguments: [
                tx.object(accountObjectId),
                tx.object(profileObjectId),
                tx.object(shopObjectId),
                tx.object(LOYALTY_PROGRAM_ID),
                tx.pure(count, 'u64'),
                tx.object(SUI_CLOCK_ID),
            ],
        });
    } else {
        // Use single purchase function for quantity == 1
        tx.moveCall({
            target: `${PACKAGE_ID}::${MODULES.LOYALTY}::record_purchase`,
            arguments: [
                tx.object(accountObjectId),
                tx.object(profileObjectId),
                tx.object(shopObjectId),
                tx.object(LOYALTY_PROGRAM_ID),
                tx.object(SUI_CLOCK_ID),
            ],
        });
    }

    return tx;
}

/**
 * Build transaction: record a purchase carrying a server attestation
 * (contracts/STAMP_AUTHORIZATION_DESIGN.md). Unlike record_purchase, this
 * cannot be minted by the customer alone — the contract verifies `signature`
 * against the pubkey held in the on-chain StampAuthority object before it
 * credits anything.
 *
 * Argument order matches record_purchase_attested exactly: account, profile,
 * shop, program, authority, signature, quantity, expires_ms, clock. There is
 * no explicit sequence argument — the contract reads account.purchases itself
 * at call time and the signature was issued to match it (api/stamp.ts).
 */
export function buildRecordPurchaseAttestedTx(
    accountObjectId: string,
    profileObjectId: string,
    shopObjectId: string,
    signatureBase64: string,
    quantity: number,
    expiresMs: number,
): TransactionBlock {
    const tx = new TransactionBlock();
    // The API returns the signature as base64; the Move call wants raw bytes.
    const signatureBytes = Array.from(Buffer.from(signatureBase64, 'base64'));
    tx.moveCall({
        target: `${PACKAGE_ID}::${MODULES.LOYALTY}::record_purchase_attested`,
        arguments: [
            tx.object(accountObjectId),
            tx.object(profileObjectId),
            tx.object(shopObjectId),
            tx.object(LOYALTY_PROGRAM_ID),
            tx.object(STAMP_AUTHORITY_ID),
            tx.pure(signatureBytes, 'vector<u8>'),
            tx.pure(quantity, 'u64'),
            tx.pure(expiresMs, 'u64'),
            tx.object(SUI_CLOCK_ID),
        ],
    });
    return tx;
}

/**
 * Build transaction: Redeem points for a voucher
 */
export function buildRedeemRewardTx(
    accountObjectId: string,
    shopObjectId: string,
    pointsCost: number,
    rewardType: string,
): TransactionBlock {
    const tx = new TransactionBlock();
    tx.moveCall({
        target: `${PACKAGE_ID}::${MODULES.LOYALTY}::redeem_reward`,
        arguments: [
            tx.object(accountObjectId),
            tx.object(shopObjectId),
            tx.object(LOYALTY_PROGRAM_ID),
            tx.pure(pointsCost),
            tx.pure(Array.from(new TextEncoder().encode(rewardType))),
            tx.object(SUI_CLOCK_ID),
        ],
    });
    return tx;
}

/**
 * Build transaction: Register a coffee shop
 */
export function buildRegisterShopTx(
    name: string,
    description: string,
    location: string,
    imageUrl: string,
): TransactionBlock {
    const tx = new TransactionBlock();
    const encode = (s: string) => Array.from(new TextEncoder().encode(s));
    tx.moveCall({
        target: `${PACKAGE_ID}::${MODULES.SHOP_REGISTRY}::register_shop`,
        arguments: [
            tx.object(SHOP_REGISTRY_ID),
            tx.pure(encode(name)),
            tx.pure(encode(description)),
            tx.pure(encode(location)),
            tx.pure(encode(imageUrl)),
            tx.object(SUI_CLOCK_ID),
        ],
    });
    return tx;
}

/**
 * Build transaction: Burn a RewardNFT to get a free coffee voucher
 */
export function buildRedeemRewardNFTTx(
    nftObjectId: string,
): TransactionBlock {
    const tx = new TransactionBlock();
    tx.moveCall({
        target: `${PACKAGE_ID}::${MODULES.LOYALTY}::redeem_reward_nft`,
        arguments: [
            tx.object(nftObjectId),
            tx.object(SUI_CLOCK_ID),
        ],
    });
    return tx;
}

/**
 * Build transaction: Redeem (burn) a voucher
 */
export function buildRedeemVoucherTx(
    voucherObjectId: string,
    shopObjectId: string,
): TransactionBlock {
    const tx = new TransactionBlock();
    tx.moveCall({
        target: `${PACKAGE_ID}::${MODULES.VOUCHER}::redeem_voucher`,
        arguments: [
            tx.object(voucherObjectId),
            tx.object(shopObjectId),
            tx.object(SUI_CLOCK_ID),
        ],
    });
    return tx;
}

/**
 * Build transaction: Mint a badge (admin only — kept for backfill).
 */
export function buildMintBadgeTx(
    badgeType: string,
    customerAddress: string,
): TransactionBlock {
    const tx = new TransactionBlock();
    tx.moveCall({
        target: `${PACKAGE_ID}::${MODULES.BADGES}::mint_badge`,
        arguments: [
            tx.object(BADGE_REGISTRY_ID),
            tx.pure(Array.from(new TextEncoder().encode(badgeType))),
            tx.pure(customerAddress),
            tx.object(SUI_CLOCK_ID),
        ],
    });
    return tx;
}

/**
 * Build transaction: Customer self-claims an earned milestone badge.
 * Eligibility (purchase count at this shop) + dedup are enforced on-chain.
 */
export function buildClaimBadgeTx(
    accountObjectId: string,
    badgeType: string,
): TransactionBlock {
    const tx = new TransactionBlock();
    tx.moveCall({
        target: `${PACKAGE_ID}::${MODULES.BADGES}::claim_badge`,
        arguments: [
            tx.object(BADGE_REGISTRY_ID),
            tx.object(accountObjectId),
            tx.pure(Array.from(new TextEncoder().encode(badgeType))),
            tx.object(SUI_CLOCK_ID),
        ],
    });
    return tx;
}

/**
 * Build transaction: Create a referral linking referrer → referee.
 * The bonus stays locked until the referee makes `purchasesRequired` purchases.
 */
export function buildCreateReferralTx(
    referrerAddress: string,
    refereeAddress: string,
    bonusPoints: number,
    purchasesRequired: number,
): TransactionBlock {
    const tx = new TransactionBlock();
    tx.moveCall({
        target: `${PACKAGE_ID}::${MODULES.REFERRAL}::create_referral`,
        arguments: [
            tx.pure(referrerAddress),
            tx.pure(refereeAddress),
            tx.pure(bonusPoints, 'u64'),
            tx.pure(purchasesRequired, 'u64'),
            tx.object(SUI_CLOCK_ID),
        ],
    });
    return tx;
}

/**
 * Build transaction: Replace the shop's on-chain reward menu (owner signs; cap must match shop).
 */
export function buildSetRewardTiersTx(
    shopObjectId: string,
    capabilityId: string,
    tiers: { pointsCost: number; rewardType: string }[],
): TransactionBlock {
    const tx = new TransactionBlock();
    tx.moveCall({
        target: `${PACKAGE_ID}::${MODULES.SHOP_REGISTRY}::set_reward_tiers`,
        arguments: [
            tx.object(shopObjectId),
            tx.object(capabilityId),
            tx.pure(tiers.map((t) => Math.round(t.pointsCost)), 'vector<u64>'),
            tx.pure(tiers.map((t) => Array.from(new TextEncoder().encode(t.rewardType)))),
        ],
    });
    return tx;
}

/**
 * Set a shop's card length — "every Nth coffee free".
 *
 * `set_card_size` has been live on mainnet since the v4 publish with no
 * owner-facing UI; this is that UI's transaction. Unlike set_reward_tiers it
 * takes NO capability object: the Move function asserts
 * `tx_context::sender(ctx) == shop_owner` directly, so the signer IS the
 * authorisation. It also asserts `free_every_n >= 2`, which is why the
 * settings input carries min={2} — a 1 would abort the transaction.
 *
 * The value stored on chain is FREE_EVERY_N (the tenth coffee), not the count
 * of earned stamps. Contract-side, `card_size(...) - 1` is what divides
 * purchases into cards; `stampsPerCard()` mirrors that on the client.
 */
export function buildSetCardSizeTx(
    shopObjectId: string,
    freeEveryN: number,
): TransactionBlock {
    const tx = new TransactionBlock();
    tx.moveCall({
        target: `${PACKAGE_ID}::${MODULES.LOYALTY}::set_card_size`,
        arguments: [
            tx.object(LOYALTY_PROGRAM_ID),
            tx.object(shopObjectId),
            tx.pure(Math.round(freeEveryN), 'u64'),
        ],
    });
    return tx;
}

/**
 * Query recent purchase events for a shop
 */
export async function queryRecentPurchases(shopAddress: string, limit = 20) {
    const events = await suiClient.queryEvents({
        query: {
            MoveEventType: EVENT_TYPES.PurchaseRecorded,
        },
        limit,
        order: 'descending',
    });

    // Filter to this shop's events
    return events.data.filter(
        (e: any) => e.parsedJson?.shop === shopAddress
    );
}
